
import logo from "./logo.svg";
import "./App.css";
import Login from "./Pages/Public/Login";
import Register from "./Pages/Public/Register";
import Cookies from "universal-cookie";
import { BrowserRouter, Routes, Navigate, Route } from "react-router-dom";
import Dashboard from "./Pages/Private/Dashboard";
import Landing from "./Pages/Public/Landing";
import ChoosePlans from "./Pages/Private/Settings/Payments/ChoosePlans";
import ViewPlans from "./Pages/Private/Settings/Payments/ViewPlans";
import SideBar from "./Components/SideBar";
import UserSettings from "./Pages/Private/Settings/UserSettings";
import BillingController from "./Pages/Private/Settings/Payments/BillingController";
import ViewSubscriptions from "./Pages/Private/Settings/Payments/ViewSubscriptions";
import EmailConfirmation from "./Pages/Public/EmailConfirmation";
import RequestPasswordChange from "./Pages/Public/RequestPasswordChange";
import PasswordChange from "./Pages/Public/PasswordChange";
import GranularCookieConsent from "./Components/Privacy/GranularCookieConsent";
import AccountSettings from "./Pages/Private/Settings/Account/AccountSettings";
import NotFound from "./Pages/Public/NotFound";
import Onboarding from "./Pages/Private/Settings/Onboarding/Onboarding";
import Team from "./Pages/Private/Settings/Team/Team";
import Audio from "./Pages/Private/Settings/Audio/Audio";
import Meetings from "./Pages/Private/Settings/Meetings/Meetings";
import Meeting from "./Pages/Private/Settings/Meetings/Meeting";
import LiveAudioRecord from "./Components/Audio/LiveAudioRecord";
import PricingPage from "./Pages/Private/Settings/Billing/PricingPage";
import RegisterByLink from "./Pages/Public/RegisterByLink";
import DownloadDocument from "./Pages/Private/Settings/TC&PP/DownloadDocument";
import PublicMeeting from "./Pages/Private/Settings/PublicMeetings/PublicMeeting";
import Analysis from "./Pages/Private/Settings/Analysis/Analysis";
import Analys from "./Pages/Private/Settings/Analysis/Analys";
import Legislativas2024 from "./Pages/Public/Legislativas2024";
import Legislativas2024Debate from "./Pages/Public/Legislativas2024Debate";
import Projects from "./Pages/Private/Settings/Projects/Projects";
import DetailProject from "./Pages/Private/Settings/Projects/DetailProject";
import ArticleDetail from "./Pages/Private/Settings/Articles/ArticleDetail";
import FullArticleView from "./Pages/Private/Settings/Articles/FullArticleView";
import ArticleBibliography from "./Pages/Private/Settings/Articles/ArticleBibliography";
import NewAnalysisHolder from "./Pages/Private/Settings/NewAnalysis/NewAnalysisHolder";
import TextCoder from "./Pages/Private/Settings/TextCoder/TextCoder";
import ResearchPipeline from "./Pages/Private/Settings/ResearchPipeline/ResearchPipeline";
import CreateResearchPipeline from "./Pages/Private/Settings/ResearchPipeline/CreateResearchPipeline";
import PipelineDetail from "./Pages/Private/Settings/ResearchPipeline/PipelineDetail";
import PipelineResults from "./Pages/Private/Settings/ResearchPipeline/PipelineResults";
import { useEffect } from "react";
import RewardsPage from "./Pages/Private/Settings/Billing/RewardsPage";
import BillingUsage from "./Pages/Private/Settings/Billing/BillingUsage";
import Banner from "./Components/Banner";
import PublicSubjectForm from "./Components/Subjects/PublicSubjectForm";
import analyticsManager from "./utils/analyticsManager";
import { cleanupCookiesBasedOnConsent } from "./utils/cookieConsent";
import { tokenManager } from "./utils/security";
import { initializeCSP } from "./utils/csp";
import { isBetaUser } from "./utils/cookies";

function PrivateOutlet({ children }) {
  const cookies = new Cookies();
  const token = cookies.get("token");
  
  // Start token refresh for authenticated users
  useEffect(() => {
    if (token) {
      tokenManager.startAutoRefresh();
    } else {
      tokenManager.stopAutoRefresh();
    }
    
    return () => {
      // Cleanup on unmount
      tokenManager.stopAutoRefresh();
    };
  }, [token]);
  
  return !!token ? <><Banner />{children}</> : <Navigate to="/login" />;
}

function BetaPrivateOutlet({ children }) {
  const cookies = new Cookies();
  const token = cookies.get("token");
  
  // Start token refresh for authenticated users
  useEffect(() => {
    if (token) {
      tokenManager.startAutoRefresh();
    } else {
      tokenManager.stopAutoRefresh();
    }
    
    return () => {
      // Cleanup on unmount
      tokenManager.stopAutoRefresh();
    };
  }, [token]);
  
  // Check authentication first, then beta access
  if (!token) {
    return <Navigate to="/login" />;
  }
  
  // Check if user is a beta user
  if (!isBetaUser()) {
    return <Navigate to="/dashboard" />;
  }
  
  return <><Banner />{children}</>;
}

function PublicOutlet({ children }) {
  const cookies = new Cookies();
  const token = cookies.get("token");
  return !!token ? <Navigate to="/dashboard" /> : children;
}

function CookieWarning() {
  const handleConsentUpdate = (preferences) => {
    // Update analytics based on new preferences
    analyticsManager.updateConsent(preferences.analytics);
    // Clean up cookies based on consent
    cleanupCookiesBasedOnConsent();
  };

  return <GranularCookieConsent onConsentUpdate={handleConsentUpdate} />;
}

function App() {

  useEffect(() => {
    // Initialize CSP with environment-based API domain
    initializeCSP();
    
    // Initialize analytics services with consent check
    analyticsManager.initialize();
  }, [])

  return (
    <BrowserRouter>
      <CookieWarning />
      <Routes >
        <Route path="/" element={<PublicOutlet><Login /></PublicOutlet>} />
        <Route path="/:join" element={<PublicOutlet><Login /></PublicOutlet>} />
        <Route path="/login" element={<PublicOutlet><Login /></PublicOutlet>} />
        <Route path="/login/:language" element={<PublicOutlet><Login /></PublicOutlet>} />
        <Route path="/register" element={<PublicOutlet><Register /></PublicOutlet>} />
        <Route path="/register/:language" element={<PublicOutlet><Register /></PublicOutlet>} />
        <Route path="/register/:language/:invite_uuid" element={<PublicOutlet><Register /></PublicOutlet>} />
        <Route path="/register_by_link/:confirmation_token" element={<PublicOutlet>
          <RegisterByLink />
        </PublicOutlet>} />
        <Route path="/legislativas-2024" element={
          <Legislativas2024 />
        } />
        <Route path="/debate-partidos-com-assento-parlamentar" element={
          <Legislativas2024Debate />
        } />
        <Route
          path="/form/:token"
          element={
            <PublicSubjectForm />
          }
        />
        <Route path="/email_confirmation" element={<EmailConfirmation />} />
        <Route path="/request_password_change" element={<RequestPasswordChange />} />
        <Route path="/change_password" element={<PasswordChange />} />
        <Route path="/onboarding" element={<Onboarding />} />

        <Route path="/downloads/:type/:language" element={<DownloadDocument />} />


        <Route
          path="/dashboard"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Dashboard"} />
              <Dashboard />
            </PrivateOutlet>
          }
        />
        <Route
          path="/dashboard/:tutorial"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Dashboard"} />
              <Dashboard />
            </PrivateOutlet>
          }
        />

        <Route
          path="/team"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Team"} />
              <Team />
            </PrivateOutlet>
          }
        />





        <Route
          path="/audio"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Audio"} />
              <Audio />
            </PrivateOutlet>
          }
        />

        <Route
          path="/record"
          element={
            <PrivateOutlet>
              <LiveAudioRecord />
            </PrivateOutlet>
          }
        />

        <Route
          path="/meetings"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Meetings"} />
              <Meetings />
            </PrivateOutlet>
          }
        />

        <Route
          path="/projects"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Projects"} />
              <Projects />
            </PrivateOutlet>
          }
        />

        <Route
          path="/project/:project_uuid"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Projects"} />
              <DetailProject />
            </PrivateOutlet>
          }
        />

        <Route
          path="/project/:project_uuid/article/:articleId"
          element={
            <PrivateOutlet>
              <ArticleDetail />
            </PrivateOutlet>
          }
        />

        <Route
          path="/project/:project_uuid/article/:articleId/full"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Projects"} />
              <FullArticleView />
            </PrivateOutlet>
          }
        />

        <Route
          path="/project/:project_uuid/bibliography"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Projects"} />
              <ArticleBibliography />
            </PrivateOutlet>
          }
        />

        <Route
          path="/meeting/public/:public_uuid"
          element={
            <PublicMeeting />
          }
        />

        <Route
          path="/meeting/:meeting_uuid"
          element={
            <PrivateOutlet>
              <SideBar />
              <Meeting />
            </PrivateOutlet>
          }
        />

        {/*Settings */}
        {/*   Account */}
        <Route
          path="/text_coder/:project_uuid/file/:analysis_uuid"
          element={
            <PrivateOutlet>
              <TextCoder />
            </PrivateOutlet>
          }
        />
        <Route
          path="/text_coder/:project_uuid"
          element={
            <PrivateOutlet>
              <TextCoder />
            </PrivateOutlet>
          }
        />

        {/* AI Researcher Routes - Beta Users Only */}
        <Route
          path="/research-pipeline"
          element={
            <BetaPrivateOutlet>
              <SideBar current_tab={"AI Researcher"} />
              <ResearchPipeline />
            </BetaPrivateOutlet>
          }
        />
        <Route
          path="/research-pipeline/create"
          element={
            <BetaPrivateOutlet>
              <SideBar current_tab={"AI Researcher"} />
              <CreateResearchPipeline />
            </BetaPrivateOutlet>
          }
        />
        <Route
          path="/research-pipeline/:pipeline_uuid"
          element={
            <BetaPrivateOutlet>
              <SideBar current_tab={"AI Researcher"} />
              <PipelineDetail />
            </BetaPrivateOutlet>
          }
        />
        <Route
          path="/research-pipeline/:pipeline_uuid/results"
          element={
            <BetaPrivateOutlet>
              <SideBar current_tab={"AI Researcher"} />
              <PipelineResults />
            </BetaPrivateOutlet>
          }
        />

        <Route
          path="/analysis"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Analysis"} />
              <Analysis />
            </PrivateOutlet>
          }
        />
        <Route
          path="/analysis/:analysis_uuid"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Projects"} />
              <NewAnalysisHolder view_mode={"private"} />
            </PrivateOutlet>
          }
        />
        <Route
          path="analysis/public/:analysis_uuid"
          element={
            <PublicOutlet>
              <NewAnalysisHolder view_mode={"public"} />
            </PublicOutlet>
          }
        />

        <Route
          path="/deprecated_analysis/:analysis_uuid"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Analysis"} />
              <Analys />
            </PrivateOutlet>
          }
        />

        <Route
          path="/account_settings"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Settings"} />
              <AccountSettings />
            </PrivateOutlet>
          }
        />
        {/*   Billing*/}
        <Route
          path="/empty_subscriptions"
          element={
            <PrivateOutlet>
              <SideBar />
              <UserSettings tabName="Plans">
                <BillingController />
              </UserSettings>
            </PrivateOutlet>
          }
        />
        <Route
          path="/view_subscriptions"
          element={
            <PrivateOutlet>
              <SideBar />
              <UserSettings tabName="Plans">
                <BillingController>
                  <ViewSubscriptions />
                </BillingController>
              </UserSettings>
            </PrivateOutlet>
          }
        />

        <Route
          path="/rewards"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Rewards"} />
              <RewardsPage />
            </PrivateOutlet>
          }
        />

        <Route
          path="/pricing"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Credits"} />
              <PricingPage />
            </PrivateOutlet>
          }
        />
        <Route
          path="/pricing/:tab"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Credits"} />
              <PricingPage />
            </PrivateOutlet>
          }
        />
        <Route
          path="/pricing/:tab/:status"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Credits"} />
              <PricingPage />
            </PrivateOutlet>
          }
        />

        <Route
          path="/billing"
          element={
            <PrivateOutlet>
              <SideBar current_tab={"Billing"} />
              <BillingUsage />
            </PrivateOutlet>
          }
        />

        <Route
          path="/plans"
          element={
            <PrivateOutlet>
              <SideBar />
              <UserSettings tabName="Plans">
                <BillingController>
                  <PricingPage />
                </BillingController>
              </UserSettings>
            </PrivateOutlet>
          }
        />

        <Route
          path="/payment_option_details"
          element={
            <PrivateOutlet>
              <SideBar />
              <UserSettings tabName="Plans">
                <BillingController forceRefresh={true}>
                  <ChoosePlans />
                </BillingController>
              </UserSettings>
            </PrivateOutlet>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>

    </BrowserRouter>
  );
}
//<BrowserRouter basename={window.location.pathname || ''}>
//<Route exact path="/login" element={Boolean(token)?<Login/>:<Navigate to="/register" replace/>}/>
export default App;
